import React, { useEffect } from "react";
import { IDataEntryLeadCount } from "../../models/LeadData.model";
import DataEntryPerformanceSkeleton from "../Skeleton/DataEntryPerformanceSkeleton";
import { fetchDataEntryLeadCount } from "../../store/slices/kanbanSlice";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../store";

interface DataEntryPerformanceProps {
  startDate: string;
  endDate?: string;
  onDateChange: (start: string, end?: string) => void;

  data: IDataEntryLeadCount[];
  loading: boolean;
  keepPreviousData?: boolean;
}

const DataEntryPerformance: React.FC<DataEntryPerformanceProps> = ({
  startDate,
  endDate,
  onDateChange,
  data,
  loading,
  keepPreviousData = false,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  useEffect(() => {
    dispatch(fetchDataEntryLeadCount({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold text-gray-800">
          Data Entry Performance
        </h2>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col sm:gap-1">
          <label className="min-w-0 text-[11px] font-medium text-slate-500">
            Start
            <input
              id="dataEntryStartDate"
              type="date"
              value={startDate}
              onChange={(e) => onDateChange(e.target.value, endDate)}
              className="mt-1 block h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
            />
          </label>
          <label className="min-w-0 text-[11px] font-medium text-slate-500">
            End
            <input
              id="dataEntryEndDate"
              type="date"
              value={endDate || ""}
              onChange={(e) =>
                onDateChange(startDate, e.target.value || undefined)
              }
              className="mt-1 block h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
            />
          </label>
        </div>
      </div>

      {loading && (!keepPreviousData || data.length === 0) ? (
        <DataEntryPerformanceSkeleton />
      ) : (
        <div className="relative">
          {loading && keepPreviousData && (
            <div
              className="absolute right-3 top-2 z-10 inline-flex items-center gap-1.5 rounded-full border border-teal-100 bg-white/95 px-2.5 py-1 text-[11px] font-medium text-teal-800 shadow-sm"
              role="status"
              aria-live="polite"
            >
              <svg
                className="h-3.5 w-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              Updating
            </div>
          )}
          <div className="max-h-[min(45vh,400px)] divide-y divide-slate-100 overflow-y-auto">
            {data.length > 0 ? (
              data.map((entry, index) => (
                <div key={index} className="py-2 px-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 text-xs font-medium">
                          {entry.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {entry.username}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-200">
                      <svg
                        className="w-3 h-3 mr-1.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {entry.count}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-5 text-center text-xs text-slate-500">
                No data entry activity found for this date
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEntryPerformance;
